import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import * as cacheUtils from '../../utils/cacheUtils';
import * as lawnData from '../../data/lawnDataLayer';
import { DATA_SOURCES } from '../../data/lawnDataLayer';
import { GRID_COLUMNS, WIDGET_DEFAULT_LAYOUT } from '../../types/lawnTypes';

/**
 * State for the Lawn Irrigation Dashboard.
 *
 * All persistence goes through `src/data/lawnDataLayer.js`, so this context never
 * knows whether it is talking to the `/api/lawn/*` endpoints or the local demo
 * copy. `source` records which one answered, purely so the UI can label demo data.
 */

const LawnDashboardContext = createContext(null);

const initialState = {
  status: 'loading', // loading | ready | error
  source: DATA_SOURCES.local,
  apiError: null,
  loadError: null,
  properties: [],
  property: null,
  sensors: [],
  forecast: [],
  ordinance: null,
  widgets: [],
  selectedSensorId: null,
  readings: [],
  isLoadingReadings: false,
  isAddingSensor: false,
};

function reducer(state, action) {
  switch (action.type) {
    case 'load_start':
      return { ...state, status: 'loading', loadError: null };

    case 'load_success':
      return {
        ...state,
        status: 'ready',
        source: action.source,
        apiError: action.apiError ?? null,
        loadError: null,
        properties: action.data.properties,
        property: action.data.property,
        sensors: action.data.sensors,
        forecast: action.data.forecast,
        ordinance: action.data.ordinance,
        widgets: action.data.widgets,
        // Drop a stale selection if that sensor no longer exists.
        selectedSensorId: action.data.sensors.some(
          (sensor) => sensor.id === state.selectedSensorId
        )
          ? state.selectedSensorId
          : null,
      };

    case 'load_failure':
      return { ...state, status: 'error', loadError: action.error };

    case 'adding_sensor':
      return { ...state, isAddingSensor: action.value };

    case 'sensor_added':
      return {
        ...state,
        sensors: [...state.sensors, action.sensor],
        selectedSensorId: action.sensor.id,
        isAddingSensor: false,
      };

    case 'sensor_updated':
      return {
        ...state,
        sensors: state.sensors.map((sensor) =>
          sensor.id === action.sensor.id ? action.sensor : sensor
        ),
      };

    case 'sensor_removed':
      return {
        ...state,
        sensors: state.sensors.filter((sensor) => sensor.id !== action.sensorId),
        selectedSensorId:
          state.selectedSensorId === action.sensorId ? null : state.selectedSensorId,
        readings: state.selectedSensorId === action.sensorId ? [] : state.readings,
      };

    case 'select_sensor':
      return { ...state, selectedSensorId: action.sensorId, readings: [] };

    case 'readings_loading':
      return { ...state, isLoadingReadings: true };

    case 'readings_loaded':
      return { ...state, readings: action.readings, isLoadingReadings: false };

    case 'widgets_changed':
      return { ...state, widgets: action.widgets };

    case 'property_updated':
      return {
        ...state,
        property: { ...state.property, ...action.updates },
        properties: state.properties.map((item) =>
          item.id === state.property?.id ? { ...item, ...action.updates } : item
        ),
      };

    default:
      return state;
  }
}

/** Username the rest of the app stores after login. */
const readUsername = () => {
  const stored = cacheUtils.get('userVal');
  if (!stored) return null;
  return stored.data || stored;
};

export function LawnDashboardProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const username = useMemo(readUsername, []);

  const load = useCallback(async () => {
    dispatch({ type: 'load_start' });
    try {
      const { data, source, apiError } = await lawnData.getDashboard(username);
      dispatch({ type: 'load_success', data, source, apiError });
    } catch (error) {
      dispatch({ type: 'load_failure', error: error.message });
    }
  }, [username]);

  useEffect(() => {
    load();
  }, [load]);

  const selectedSensor = useMemo(
    () => state.sensors.find((sensor) => sensor.id === state.selectedSensorId) || null,
    [state.sensors, state.selectedSensorId]
  );

  // Readings back the sensor detail sparkline. The ref keeps a rename or a pin
  // move from triggering a redundant fetch for the sensor already loaded.
  const loadedReadingsFor = useRef(null);

  useEffect(() => {
    if (!selectedSensor) {
      loadedReadingsFor.current = null;
      return undefined;
    }
    if (loadedReadingsFor.current === selectedSensor.id) {
      return undefined;
    }

    loadedReadingsFor.current = selectedSensor.id;
    let cancelled = false;
    dispatch({ type: 'readings_loading' });
    lawnData
      .getSensorReadings(username, selectedSensor, { source: state.source })
      .then((readings) => {
        if (!cancelled) dispatch({ type: 'readings_loaded', readings });
      })
      .catch(() => {
        if (!cancelled) dispatch({ type: 'readings_loaded', readings: [] });
      });

    return () => {
      cancelled = true;
    };
  }, [selectedSensor, username, state.source]);

  const addSensor = useCallback(
    async (draft) => {
      if (!state.property) return;
      dispatch({ type: 'adding_sensor', value: true });
      try {
        const sensor = await lawnData.addSensor(username, state.property.id, draft, {
          source: state.source,
        });
        dispatch({ type: 'sensor_added', sensor });
      } catch (error) {
        console.error('Could not add sensor', error);
        dispatch({ type: 'adding_sensor', value: false });
      }
    },
    [username, state.property, state.source]
  );

  const updateSensor = useCallback(
    async (sensorId, updates) => {
      const sensor = await lawnData.updateSensor(username, sensorId, updates, {
        source: state.source,
        propertyId: state.property?.id,
      });
      if (sensor) dispatch({ type: 'sensor_updated', sensor });
    },
    [username, state.source, state.property]
  );

  const removeSensor = useCallback(
    async (sensorId) => {
      await lawnData.removeSensor(username, sensorId, { source: state.source });
      dispatch({ type: 'sensor_removed', sensorId });
    },
    [username, state.source]
  );

  const selectSensor = useCallback((sensor) => {
    dispatch({ type: 'select_sensor', sensorId: sensor?.id ?? null });
  }, []);

  const saveWidgets = useCallback(
    async (widgets) => {
      // Update immediately so dragging feels direct; persist in the background.
      dispatch({ type: 'widgets_changed', widgets });
      try {
        await lawnData.saveWidgets(username, widgets, { source: state.source });
      } catch (error) {
        console.error('Could not persist dashboard layout', error);
      }
    },
    [username, state.source]
  );

  const addWidget = useCallback(
    (widgetType) => {
      const defaults = WIDGET_DEFAULT_LAYOUT[widgetType] || { width: 4, height: 4 };
      const nextRow = state.widgets.reduce(
        (max, widget) => Math.max(max, widget.y + widget.height),
        0
      );
      const widget = {
        id: `wid_${Date.now().toString(36)}`,
        widgetType,
        x: 0,
        y: nextRow,
        width: Math.min(GRID_COLUMNS, defaults.width),
        height: defaults.height,
        configJson: '{}',
      };
      saveWidgets([...state.widgets, widget]);
    },
    [state.widgets, saveWidgets]
  );

  const removeWidget = useCallback(
    (widgetId) => {
      saveWidgets(state.widgets.filter((widget) => widget.id !== widgetId));
    },
    [state.widgets, saveWidgets]
  );

  const setPropertyImage = useCallback(
    async (imageUrl) => {
      if (!state.property) return;
      dispatch({ type: 'property_updated', updates: { imageUrl } });
      try {
        await lawnData.updateProperty(
          username,
          state.property.id,
          { imageUrl },
          { source: state.source }
        );
      } catch (error) {
        console.error('Could not save property image', error);
      }
    },
    [username, state.property, state.source]
  );

  const resetDashboard = useCallback(async () => {
    dispatch({ type: 'load_start' });
    try {
      const { data, source } = await lawnData.resetDashboard(username, { source: state.source });
      dispatch({ type: 'load_success', data, source, apiError: state.apiError });
    } catch (error) {
      dispatch({ type: 'load_failure', error: error.message });
    }
  }, [username, state.source, state.apiError]);

  const value = useMemo(
    () => ({
      ...state,
      username,
      selectedSensor,
      isDemoData: state.source === DATA_SOURCES.local,
      reload: load,
      addSensor,
      updateSensor,
      removeSensor,
      selectSensor,
      saveWidgets,
      addWidget,
      removeWidget,
      setPropertyImage,
      resetDashboard,
    }),
    [
      state,
      username,
      selectedSensor,
      load,
      addSensor,
      updateSensor,
      removeSensor,
      selectSensor,
      saveWidgets,
      addWidget,
      removeWidget,
      setPropertyImage,
      resetDashboard,
    ]
  );

  return (
    <LawnDashboardContext.Provider value={value}>{children}</LawnDashboardContext.Provider>
  );
}

LawnDashboardProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useLawnDashboard() {
  const context = useContext(LawnDashboardContext);
  if (!context) {
    throw new Error('useLawnDashboard must be used inside a LawnDashboardProvider');
  }
  return context;
}
